/**
 * As regras do calendário, sem tela: a semana, a grade, a posição de cada evento, a validação e o
 * registro que o "Salvar" grava. Cada função é o porte literal do legado (`admin.html:4326-4697`),
 * com a linha ao lado.
 *
 * POR QUE SEPARADO DA TELA: são as partes que mais escondem defeito (fuso, sobreposição, zerar a
 * resposta da cliente), e sem React elas se medem com o caso de borda escrito à mão.
 */

// O formato AAAA-MM-DD pela hora local, idêntico ao legado (`admin.html:4352`).
import { formatarDataYMD } from '../../comum/formatos.js';
// Os tipos da área de dados: o compromisso lido, o lead do campo e o registro gravado.
import type {
  CompromissoDaSemana,
  LeadParaCompromisso,
  RegistroDeCompromisso,
} from '@studiolu/dados/areas/compromissos';

// A primeira linha de hora da grade: 07:00 (`CAL_HORA_INICIO`, `admin.html:4334`).
export const CAL_HORA_INICIO = 7;
// A grade vai até antes das 23:00, então a última linha é 22:00 (`CAL_HORA_FIM`, `4335`).
export const CAL_HORA_FIM = 23;
// A altura de uma hora, em pixels, que decide onde cada evento cai (`CAL_ALTURA_HORA`, `4336`).
export const CAL_ALTURA_HORA = 48;
// O rótulo curto de cada dia, de domingo a sábado (`CAL_DIAS_SEMANA`, `4337`).
export const CAL_DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;
// Sempre a semana inteira, em qualquer largura (`calNumDias`, `4341`).
export const NUM_DIAS = 7;
// A coluna de dia nunca fica mais estreita que 100 px; no celular isso liga a rolagem (`4471`).
export const LARGURA_MIN_COLUNA = 100;
// A coluna das horas, à esquerda (`gridColunas`, `4473`).
export const LARGURA_COLUNA_HORAS = 40;
// O evento mais curto ainda desenha 20 px, para caber o título (`admin.html:4436`).
const ALTURA_MIN_EVENTO = 20;

/** O domingo da semana de uma data, à meia-noite local (`inicioDoPeriodo`, `admin.html:4345`). */
export function inicioDoPeriodo(data: Date): Date {
  const d = new Date(data);
  // Meia-noite local: a semana começa no começo do domingo, e não na hora do clique.
  d.setHours(0, 0, 0, 0);
  // Volta até o domingo (`getDay` é 0 no domingo).
  d.setDate(d.getDate() - d.getDay());
  return d;
}

/** O dia seguinte ao último da semana: o fim excluído da consulta (`admin.html:4377`). */
export function fimDoPeriodo(inicio: Date): Date {
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + NUM_DIAS);
  return fim;
}

/** Soma dias a uma data sem mexer na original (o `setDate` sobre cópia do legado). */
export function somarDias(data: Date, dias: number): Date {
  const d = new Date(data);
  d.setDate(d.getDate() + dias);
  return d;
}

/** "14 de set. - 20 de set. de 2026", como o título da barra (`admin.html:4403-4405`). */
export function rotuloDoIntervalo(inicio: Date): string {
  // O último dia mostrado, e não o dia excluído da consulta.
  const ultimo = somarDias(inicio, NUM_DIAS - 1);
  // Dia com dois dígitos e mês curto, no formato brasileiro do navegador.
  const curto = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  return `${curto(inicio)} - ${curto(ultimo)} de ${ultimo.getFullYear()}`;
}

/** As horas da grade: 7 a 22 (`admin.html:4407-4408`). */
export function horasDaGrade(): number[] {
  const horas: number[] = [];
  for (let h = CAL_HORA_INICIO; h < CAL_HORA_FIM; h++) horas.push(h);
  return horas;
}

/** Duas datas no mesmo dia local, como a comparação por ano, mês e dia do legado (`4424`). */
export function mesmoDia(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Um evento pronto para desenhar: a posição e o que ele mostra. */
export interface EventoNaGrade {
  readonly compromisso: CompromissoDaSemana;
  // Distância do topo da coluna, em pixels.
  readonly topo: number;
  // Altura, em pixels.
  readonly altura: number;
  // Largura e posição, em porcentagem da coluna, pela faixa de sobreposição.
  readonly larguraPct: number;
  readonly esquerdaPct: number;
  // O que aparece em negrito: o título, ou o nome do lead, ou "(sem título)".
  readonly tituloExibido: string;
  // O nome do lead, quando há.
  readonly nomeLead: string | null;
  // A hora de início, HH:MM.
  readonly hora: string;
  // A dica do mouse: título e lead separados por ponto médio.
  readonly dica: string;
  // O tipo de cor: com lead, manual, confirmado ou recusado (`admin.html:4442-4445`).
  readonly tom: 'lead' | 'manual' | 'confirmado' | 'recusado';
}

/**
 * Os eventos de um dia, em ordem de início, cada um na primeira faixa livre, como as "lanes" do
 * legado (`admin.html:4423-4452`): o compromisso pega a primeira faixa cujo último compromisso já
 * terminou antes de ele começar, e todas as faixas do dia dividem a largura por igual.
 */
export function eventosDoDia(compromissos: readonly CompromissoDaSemana[], dia: Date): EventoNaGrade[] {
  // Só os do dia, pela data local, em ordem de início.
  const doDia = compromissos
    .filter((c) => mesmoDia(new Date(c.data_hora_inicio), dia))
    .sort((a, b) => new Date(a.data_hora_inicio).getTime() - new Date(b.data_hora_inicio).getTime());

  // O fim do último compromisso de cada faixa.
  const faixasFim: Date[] = [];
  const posicionados = doDia.map((c) => {
    const inicio = new Date(c.data_hora_inicio);
    const fim = new Date(inicio.getTime() + c.duracao_minutos * 60000);
    // A primeira faixa livre; sem nenhuma, abre uma nova.
    let faixa = faixasFim.findIndex((f) => f <= inicio);
    if (faixa === -1) {
      faixa = faixasFim.length;
      faixasFim.push(fim);
    } else {
      faixasFim[faixa] = fim;
    }
    return { c, inicio, faixa };
  });
  // Quantas faixas o dia usou, no mínimo uma.
  const totalFaixas = Math.max(1, ...posicionados.map((p) => p.faixa + 1));

  return posicionados.map(({ c, inicio, faixa }) => {
    // Minutos desde as 07:00, que viram pixels pela altura da hora.
    const minutosDesdeInicio = (inicio.getHours() - CAL_HORA_INICIO) * 60 + inicio.getMinutes();
    const larguraPct = 100 / totalFaixas;
    // O legado lê também `leads_funil.email`, que a consulta não pede; vale só o nome (`4440`).
    const nomeLead = c.leads_funil?.nome || null;
    return {
      compromisso: c,
      topo: (minutosDesdeInicio / 60) * CAL_ALTURA_HORA,
      // Dois pixels a menos, para dois eventos seguidos não se encostarem (`4436`).
      altura: Math.max(ALTURA_MIN_EVENTO, (c.duracao_minutos / 60) * CAL_ALTURA_HORA - 2),
      larguraPct,
      esquerdaPct: faixa * larguraPct,
      tituloExibido: c.titulo || nomeLead || '(sem título)',
      nomeLead,
      hora: inicio.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      dica: [c.titulo, nomeLead].filter(Boolean).join(' · '),
      // A resposta da cliente vence a cor de manual, como a classe que vem por último no legado.
      tom:
        c.confirmacao_cliente === 'confirmado'
          ? 'confirmado'
          : c.confirmacao_cliente === 'recusado'
            ? 'recusado'
            : c.lead_id
              ? 'lead'
              : 'manual',
    };
  });
}

// Dia/mês e hora/minuto, com dois dígitos, para a resposta da cliente (`admin.html:4558`).
const FORMATO_RESPOSTA: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

/** A frase da resposta da cliente na edição (`formatarStatusConfirmacao`, `admin.html:4555-4568`). */
export function formatarStatusConfirmacao(c: CompromissoDaSemana): string {
  // Sem pedido de confirmação, nada a dizer.
  if (!c.pedir_confirmacao_cliente) return '';
  // A hora da resposta, quando existe.
  const quando = c.confirmacao_respondida_em
    ? new Date(c.confirmacao_respondida_em).toLocaleString('pt-BR', FORMATO_RESPOSTA)
    : '';
  if (c.confirmacao_cliente === 'confirmado') return `Cliente confirmou${quando ? ` em ${quando}` : ''}.`;
  if (c.confirmacao_cliente === 'recusado') return `Cliente não poderá comparecer${quando ? ` (respondeu em ${quando})` : ''}.`;
  return 'Aguardando resposta da cliente.';
}

/** O texto de cada lead no campo "Lead", com o sufixo de perdido ou finalizado (`admin.html:4369-4372`). */
export function rotuloDoLead(lead: LeadParaCompromisso): string {
  // A chave é `finalizada`, no feminino, como o status do funil; o sufixo é no masculino.
  const sufixo: Readonly<Record<string, string>> = { perdido: ' · perdido', finalizada: ' · finalizado' };
  return `${lead.nome || lead.email || '(sem nome)'}${sufixo[lead.status] || ''}`;
}

/** As durações do campo "Duração", na ordem do legado (`admin.html:2870-2878`). */
export const OPCOES_DURACAO = [
  { valor: '15', rotulo: '15 minutos' },
  { valor: '30', rotulo: '30 minutos' },
  { valor: '45', rotulo: '45 minutos' },
  { valor: '60', rotulo: '1 hora' },
  { valor: '90', rotulo: '1h30' },
  { valor: '120', rotulo: '2 horas' },
  { valor: '180', rotulo: '3 horas' },
] as const;

/** Os avisos do campo "Avisar por e-mail", sem o "Não avisar", que é a opção vazia (`2883-2889`). */
export const OPCOES_AVISO = [
  { valor: '15', rotulo: '15 minutos antes' },
  { valor: '30', rotulo: '30 minutos antes' },
  { valor: '60', rotulo: '1 hora antes' },
  { valor: '1440', rotulo: '1 dia antes' },
] as const;

/** O que o modal guarda enquanto a pessoa preenche: os valores crus de cada campo, em texto. */
export interface CamposDoCompromisso {
  readonly leadId: string;
  readonly pedirConfirmacao: boolean;
  readonly titulo: string;
  readonly data: string;
  readonly hora: string;
  readonly duracao: string;
  readonly aviso: string;
  readonly observacoes: string;
}

/** O modal novo, vazio ou com o dia e a hora clicados (`abrirModalCompromisso`, `admin.html:4572-4588`). */
export function camposNovos(dia?: Date, hora?: number): CamposDoCompromisso {
  return {
    leadId: '',
    pedirConfirmacao: false,
    titulo: '',
    // Hoje, ou o dia da linha clicada.
    data: formatarDataYMD(dia ?? new Date()),
    // A hora cheia da linha clicada, ou 09:00.
    hora: hora !== undefined ? `${String(hora).padStart(2, '0')}:00` : '09:00',
    // 1 hora, a opção marcada no HTML do legado.
    duracao: '60',
    aviso: '',
    observacoes: '',
  };
}

/** O que o campo "Lead" fica mostrando e valendo na edição. */
export interface LeadNoCampo {
  // O valor que o "Salvar" lê: o `select.value` do legado.
  readonly valor: string;
  // `true` quando o campo fica sem opção nenhuma escolhida, em branco (`selectedIndex` -1).
  readonly emBranco: boolean;
}

/**
 * O campo "Lead" da edição, como o `<select>` do legado o deixa (`admin.html:4594`,
 * `select.value = c.lead_id || ''`).
 *
 * DEFEITO DO LEGADO REPRODUZIDO, por decisão do fundador, e isolado AQUI: quando o lead do
 * compromisso não está entre as opções do campo (lead criado depois do login, porque a lista é
 * lida uma vez só, ou a leitura dos leads falhou), o `select.value` recebe um valor sem opção, e o
 * navegador deixa o campo EM BRANCO com valor vazio (medido no Chrome em 19/09/2026 pela
 * verificação da A1: `value ""`, `selectedIndex -1`). Daí em diante tudo lê o vazio: a confirmação
 * da cliente some (`4553`) e o "Salvar" grava `lead_id: null` e `pedir_confirmacao_cliente: false`
 * (`4631`, `4644`), desvinculando o lead em silêncio. Corrigir é devolver `String(leadId)` no
 * último ramo, e só aqui.
 *
 * `lista` é o que o campo tem: `undefined` antes de a leitura voltar e `null` se ela falhou, e nos
 * dois casos o legado só tem a opção "(nenhum)".
 */
export function leadNoCampo(leadId: number | null, lista: readonly LeadParaCompromisso[] | null | undefined): LeadNoCampo {
  // Sem lead, o valor vazio tem opção ("(selecione)" ou "(nenhum)"), e o campo mostra ela.
  if (!leadId) return { valor: '', emBranco: false };
  // O lead está entre as opções: o campo o mostra, como qualquer escolha.
  if (lista?.some((l) => String(l.id) === String(leadId))) return { valor: String(leadId), emBranco: false };
  // O defeito: valor sem opção vira campo em branco, valendo vazio.
  return { valor: '', emBranco: true };
}

/**
 * O modal de edição, preenchido com o compromisso (`abrirModalCompromissoEdicao`, `4590-4607`). O
 * lead entra como o campo o deixa (`leadNoCampo`, acima), e não como está no banco.
 */
export function camposDaEdicao(c: CompromissoDaSemana, lead: LeadNoCampo): CamposDoCompromisso {
  const inicio = new Date(c.data_hora_inicio);
  return {
    leadId: lead.valor,
    pedirConfirmacao: !!c.pedir_confirmacao_cliente,
    titulo: c.titulo || '',
    data: formatarDataYMD(inicio),
    // HH:MM pela hora local, como `toTimeString().slice(0, 5)`.
    hora: inicio.toTimeString().slice(0, 5),
    duracao: String(c.duracao_minutos),
    // Zero e nulo viram "Não avisar", como o `c.avisar_antes_minutos ? ... : ''` do legado.
    aviso: c.avisar_antes_minutos ? String(c.avisar_antes_minutos) : '',
    observacoes: c.descricao || '',
  };
}

/** A primeira frase de erro do "Salvar", na ordem do legado, ou `null` quando pode gravar (`4625-4634`). */
export function validarCompromisso(campos: CamposDoCompromisso): string | null {
  if (!campos.data || !campos.hora) return 'Preencha data e hora.';
  // "HH:MM" com zero à esquerda: comparar texto é o mesmo que comparar hora (`admin.html:4627`).
  if (campos.hora > '22:00') return 'Não é possível marcar compromissos depois das 22h.';
  if (!campos.leadId && !campos.titulo.trim()) return 'Preencha o lead e/ou o título.';
  return null;
}

/** O registro e a decisão do e-mail, exatamente como o "Salvar" do legado (`admin.html:4635-4668`). */
export function montarRegistro(
  campos: CamposDoCompromisso,
  original: CompromissoDaSemana | null,
): { readonly registro: RegistroDeCompromisso; readonly precisaNotificarCliente: boolean } {
  const tituloManual = campos.titulo.trim();
  const base: RegistroDeCompromisso = {
    lead_id: campos.leadId ? parseInt(campos.leadId, 10) : null,
    titulo: tituloManual || null,
    descricao: campos.observacoes || null,
    // A data e a hora digitadas são locais; o ISO leva o fuso, como no legado.
    data_hora_inicio: new Date(`${campos.data}T${campos.hora}:00`).toISOString(),
    duracao_minutos: parseInt(campos.duracao, 10),
    avisar_antes_minutos: campos.aviso ? parseInt(campos.aviso, 10) : null,
    // O lembrete volta a valer para o horário salvo.
    lembrete_enviado_em: null,
    // Sem lead não há quem confirme: grava `false` mesmo com a marca ligada antes.
    pedir_confirmacao_cliente: campos.leadId ? campos.pedirConfirmacao : false,
  };

  // Sem pedido de confirmação, nada a zerar nem a avisar.
  if (!base.pedir_confirmacao_cliente) return { registro: base, precisaNotificarCliente: false };
  // Compromisso novo com confirmação: sempre avisa a cliente.
  if (!original) return { registro: base, precisaNotificarCliente: true };

  // Mudou data e hora, título ou duração? A resposta anterior não vale para o horário novo.
  const mudouAlgoRelevante =
    new Date(original.data_hora_inicio).getTime() !== new Date(base.data_hora_inicio).getTime() ||
    (original.titulo || null) !== base.titulo ||
    original.duracao_minutos !== base.duracao_minutos;
  // Zera a resposta: ela vê o aviso de novo no portal.
  const registro: RegistroDeCompromisso = mudouAlgoRelevante
    ? { ...base, confirmacao_cliente: null, confirmacao_respondida_em: null }
    : base;
  // Avisa quando a confirmação foi ligada agora, ou quando a resposta foi zerada.
  return { registro, precisaNotificarCliente: !original.pedir_confirmacao_cliente || mudouAlgoRelevante };
}
